import { useState, useCallback, useRef, useEffect } from 'react';
import { debugLog } from '../utils/debugLog';

/**
 * Compara dos valores de campo de forma flexible para detectar si hubo un cambio real.
 * - Objetos (p. ej. archivos) se comparan por referencia: siempre son "distintos" si no es el mismo objeto.
 * - null/undefined/'' se consideran equivalentes entre sí (campo vacío), pero distintos de cualquier valor real.
 * - Números y strings numéricamente equivalentes ("5" vs 5) se consideran iguales.
 * Evita falsos positivos como 5 !== "5" (baseline numérico vs input de texto) marcando cambios que no existen.
 */
const valoresSonIguales = (a, b) => {
  if ((a !== null && typeof a === 'object') || (b !== null && typeof b === 'object')) {
    return a === b;
  }
  const aVacio = a === null || a === undefined || a === '';
  const bVacio = b === null || b === undefined || b === '';
  if (aVacio && bVacio) return true;
  if (aVacio || bVacio) return false;

  const aNum = typeof a === 'number' ? a : Number(String(a).trim());
  const bNum = typeof b === 'number' ? b : Number(String(b).trim());
  if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum === bNum;

  return String(a).trim() === String(b).trim();
};

/**
 * Hook personalizado para el seguimiento de cambios del usuario en el formulario
 * Rastrea qué campos han sido modificados por el usuario y mantiene un historial
 */
export const useSeguimientoCambios = () => {
  const [camposModificados, setCamposModificados] = useState(new Map());
  const [totalCambios, setTotalCambios] = useState(0);
  const valoresOriginales = useRef(new Map());

  /**
   * Actualiza el total de cambios cuando cambia el Map
   */
  useEffect(() => {
    debugLog('🔍 [DEBUG HOOK] useEffect totalCambios:', {
      camposModificadosSize: camposModificados.size,
      totalCambiosAnterior: totalCambios
    });
    setTotalCambios(camposModificados.size);
  }, [camposModificados]);

  /**
   * Establece los valores originales del formulario (valores iniciales/prellenados)
   * @param {Object} valoresIniciales - Objeto con los valores iniciales del formulario
   */
  const establecerValoresOriginales = useCallback((valoresIniciales) => {
    debugLog('🔍 [DEBUG HOOK] establecerValoresOriginales llamado:', valoresIniciales);
    const nuevosValoresOriginales = new Map();
    Object.entries(valoresIniciales).forEach(([campo, valor]) => {
      nuevosValoresOriginales.set(campo, valor);
    });
    valoresOriginales.current = nuevosValoresOriginales;
    debugLog('🔍 [DEBUG HOOK] valoresOriginales establecidos:', valoresOriginales.current);
    // // debugLog('📊 [SEGUIMIENTO] Valores originales establecidos:', valoresIniciales);
  }, []);

  /**
   * Establece valores originales de forma parcial sin sobrescribir los existentes por defecto
   * @param {Object} valoresParciales - Objeto con los valores originales a fusionar
   * @param {Object} opciones - { sobrescribirExistentes: boolean }
   */
  const establecerValoresOriginalesParciales = useCallback((valoresParciales = {}, opciones = {}) => {
    const { sobrescribirExistentes = false } = opciones;
    const actuales = valoresOriginales.current;

    Object.entries(valoresParciales).forEach(([campo, valor]) => {
      const existe = actuales.has(campo);
      if (!existe || sobrescribirExistentes) {
        actuales.set(campo, valor);
      }
    });

    debugLog('🔍 [DEBUG HOOK] valoresOriginales (parciales) fusionados:', valoresOriginales.current);
  }, []);

  /**
   * Registra un cambio en un campo del formulario
   * @param {string} nombreCampo - Nombre del campo que cambió
   * @param {any} nuevoValor - Nuevo valor del campo
   * @param {Object} pregunta - Objeto de la pregunta con metadatos
   */
  const registrarCambio = useCallback((nombreCampo, nuevoValor, pregunta) => {
    debugLog('🔍 [DEBUG HOOK] registrarCambio llamado:', {
      nombreCampo,
      nuevoValor,
      pregunta: pregunta?.label || 'Sin label',
      tipo: pregunta?.type
    });
    
    const valorOriginal = valoresOriginales.current.get(nombreCampo);
    debugLog('🔍 [DEBUG HOOK] Comparando valores:', {
      nombreCampo,
      valorOriginal,
      nuevoValor,
      sonIguales: valoresSonIguales(valorOriginal, nuevoValor)
    });

    // Verificar si realmente hay un cambio (comparación flexible: evita falsos positivos
    // por diferencia de tipo, p. ej. baseline numérico 5 vs input de texto "5")
    const hayRealmenteCambio = !valoresSonIguales(valorOriginal, nuevoValor);
    debugLog('🔍 [DEBUG HOOK] ¿Hay cambio real?:', hayRealmenteCambio);
    
    setCamposModificados(prev => {
      const nuevoCamposModificados = new Map(prev);
      debugLog('🔍 [DEBUG HOOK] Estado anterior camposModificados:', prev.size);
      
      if (hayRealmenteCambio) {
        // Registrar el cambio
        nuevoCamposModificados.set(nombreCampo, {
          valorOriginal,
          valorNuevo: nuevoValor,
          pregunta,
          timestampCambio: Date.now(),
          config: {
            tabla: pregunta.config_tabla || null,
            columna: pregunta.config_columna || null
          }
        });
        debugLog('🔍 [DEBUG HOOK] Campo agregado a cambios:', nombreCampo);
      } else {
        // Si el valor volvió al original, remover del seguimiento
        if (nuevoCamposModificados.has(nombreCampo)) {
          nuevoCamposModificados.delete(nombreCampo);
          debugLog('🔍 [DEBUG HOOK] Campo removido de cambios:', nombreCampo);
        }
      }
      
      debugLog('🔍 [DEBUG HOOK] Nuevo estado camposModificados:', nuevoCamposModificados.size);
      return nuevoCamposModificados;
    });
  }, []);

  /**
   * Obtiene la estructura de datos para enviar al servidor
   * @param {Object} infoGeneral - Información general con id_sic_informacion_general e id_kobo_ficha_principal
   * @returns {Object} Estructura de datos para la API
   */
  const obtenerDatosParaEnvio = useCallback((infoGeneral) => {
    const camposModificadosArray = Array.from(camposModificados.values()).map(cambio => {
      const esArchivo = cambio.valorNuevo && typeof cambio.valorNuevo === 'object' && cambio.valorNuevo.isFile;
      
      return {
        type: cambio.pregunta.type,
        name: cambio.pregunta.name,
        value: esArchivo ? cambio.valorNuevo.name : cambio.valorNuevo,
        config: cambio.config,
        ...(esArchivo && { isFile: true })
      };
    });

    // Construir el objeto de archivos para envío multipart (clave: nombre del campo, valor: File)
    const files = {};
    Array.from(camposModificados.values()).forEach(cambio => {
      const esArchivo = cambio.valorNuevo && typeof cambio.valorNuevo === 'object' && cambio.valorNuevo.isFile;
      if (esArchivo && cambio.valorNuevo.file) {
        files[cambio.pregunta.name] = cambio.valorNuevo.file;
      }
    });

    return {
      campos_modificados: camposModificadosArray,
      id_sic_informacion_general: infoGeneral?.id_sic_informacion_general || null,
      id_kobo_ficha_principal: infoGeneral?.id_kobo_ficha_principal || null,
      // Incluir archivos para que formsService.saveGroupData envíe como multipart/form-data
      files
    };
  }, [camposModificados]);

  /**
   * Limpia todos los cambios registrados
   */
  const limpiarCambios = useCallback(() => {
    setCamposModificados(new Map());
    setTotalCambios(0);
    // debugLog('📊 [SEGUIMIENTO] Cambios limpiados');
  }, []);

  /**
   * Obtiene un resumen de los cambios realizados
   * @returns {Array} Array con resumen de cambios
   */
  const obtenerResumenCambios = useCallback(() => {
    return Array.from(camposModificados.entries()).map(([nombreCampo, cambio]) => ({
      campo: nombreCampo,
      etiqueta: cambio.pregunta.label || nombreCampo,
      tipo: cambio.pregunta.type,
      valorOriginal: cambio.valorOriginal,
      valorNuevo: cambio.valorNuevo,
      timestamp: cambio.timestampCambio
    }));
  }, [camposModificados]);

  return {
    // Estados
    camposModificados,
    totalCambios,
    
    // Funciones
    establecerValoresOriginales,
    establecerValoresOriginalesParciales,
    registrarCambio,
    obtenerDatosParaEnvio,
    limpiarCambios,
    obtenerResumenCambios,
    
    // Utilidades
    tieneCambios: totalCambios > 0
  };
};

export default useSeguimientoCambios;